import { createContext, useContext, useState } from "react";

import iso from "i18n-iso-countries";
import { Controller, useForm } from "react-hook-form";
import PhoneInput from "react-phone-input-2";

import { Button, Input, Modal, Text, toastSuccess } from "@alpacahq/ui/old";

import { useGetIsLegalEntity, useMutationRequest } from "~/v2/api/hooks";
import {
  confirmEmail,
  type OwnerDetails,
  patchDetails,
  requestEmail,
} from "~/v2/api/rest/account";
import { Field } from "~/v2/components/form";
import { weakEncrypt } from "~/v2/utils";
import type { SettingsTabProps } from "../index";

enum Step {
  None,
  Request,
  Confirm,
}

interface UpdateEmailContext {
  step?: Step;
  email: string;
  setStep: (step: Step) => void;
}

// create the context
const UpdateEmailContext = createContext<UpdateEmailContext>({
  email: "",
  step: Step.None,
  setStep: () => null,
});

export const Confirm = () => {
  const ctx = useContext(UpdateEmailContext);

  const { control, handleSubmit, reset } = useForm<{ code: string }>({
    mode: "all",
    reValidateMode: "onChange",
  });

  const onClose = () => {
    ctx.setStep(Step.None);
    reset();
  };

  const mutation = useMutationRequest(({ code }: { code: string }) =>
    confirmEmail(code),
  );

  const onSubmit = (data: { code: string }) => {
    mutation.mutate(data, {
      onSuccess: () => {
        toastSuccess("Your email has been updated");
        // done
        onClose();

        // reset step
        ctx.setStep(Step.None);
      },
    });
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} id="form--confirm">
      <Modal
        title="Verify change"
        isOpen={ctx.step === Step.Confirm}
        onClose={onClose}
        controls={
          <div className="flex items-center justify-between gap-4">
            <Button variant="ghost" onClick={onClose}>
              Close
            </Button>
            <Button type="submit" form="form--confirm">
              Submit
            </Button>
          </div>
        }
      >
        <div>
          <Text className="mb-4">
            Please enter the code received in your email
          </Text>
          <Controller
            name="code"
            control={control}
            rules={{
              required: true,
            }}
            render={({ field }) => <Input {...field} placeholder="code" />}
          />
        </div>
      </Modal>
    </form>
  );
};

export const Request = () => {
  const ctx = useContext(UpdateEmailContext);

  const { control, handleSubmit, reset } = useForm<{
    email: string;
    a: string;
  }>({
    mode: "all",
    reValidateMode: "onChange",
    defaultValues: {
      email: ctx.email,
    },
  });

  const onClose = () => {
    ctx.setStep(Step.None);
    reset();
  };

  const mutation = useMutationRequest(
    ({ email, a }: { email: string; a: string }) =>
      requestEmail(email.toLocaleLowerCase(), weakEncrypt(a)),
  );

  const onSubmit = ({ a }: { a: string }) => {
    mutation.mutate(
      { email: ctx.email, a },
      {
        onSuccess: () => {
          toastSuccess("Email sent, check your email for a code");

          ctx.setStep(Step.Confirm);
          reset();
        },
      },
    );
  };

  return (
    <>
      <form onSubmit={handleSubmit(onSubmit)} id="form--request">
        <Modal
          title="Authentication check"
          isOpen={ctx.step === Step.Request}
          onClose={onClose}
          controls={
            <div className="flex items-center justify-between gap-4">
              <Button variant="ghost" onClick={onClose}>
                Close
              </Button>
              <Button type="submit" form="form--request">
                Submit
              </Button>
            </div>
          }
        >
          <div>
            <Text className="mb-4">Please enter your password</Text>
            <Controller
              name="a"
              control={control}
              rules={{ required: true }}
              render={({ field }) => (
                <Input type="password" placeholder="password" {...field} />
              )}
            />
          </div>
        </Modal>
      </form>
      <Confirm />
    </>
  );
};

type BasicFields = {
  email: string;
  phone_number: string;
};

export default ({ account, details, hasOnboarded }: SettingsTabProps) => {
  const { isLegalEntity } = useGetIsLegalEntity();

  const {
    watch,
    control,
    handleSubmit,
    formState: { isDirty, touchedFields },
  } = useForm<BasicFields>({
    mode: "all",
    reValidateMode: "onChange",
    defaultValues: {
      email: account.email,
      phone_number: details.phone_number ?? "",
    },
  });

  const [step, setStep] = useState(Step.None);

  // this mutation is really only for the phone number
  const mutation = useMutationRequest((payload: Partial<OwnerDetails>) =>
    patchDetails(account.id, { phone_number: payload.phone_number }),
  );

  const onSubmit = (data: BasicFields) => {
    // if has been touched
    touchedFields.phone_number &&
      // if phone_number is different from the default value
      data.phone_number !== details.phone_number &&
      // then update the phone number
      mutation.mutate(
        { ...data },
        {
          onSuccess: () => {
            toastSuccess("Phone number updated");
          },
        },
      );

    // if has been touched
    touchedFields.email &&
      // if email is different from the default value
      data.email !== account.email &&
      // Don't allow email updates for business accounts
      !isLegalEntity &&
      // then update the email
      setStep(Step.Request);
  };

  return (
    <UpdateEmailContext.Provider
      value={{ email: watch("email"), step, setStep }}
    >
      <div className="w-full w-max-[600px]">
        <form onSubmit={handleSubmit(onSubmit)} id="form--basic">
          <div className="flex flex-col">
            <h2 className="mb-5 font-bold text-xl">Details</h2>
            <div className="mb-2 flex items-center justify-start">
              <Text className="w-[50%] whitespace-nowrap pr-4">
                Account Number
              </Text>
              <Text className="grow text-[#60676a]">
                {account.account_number}
              </Text>
            </div>
            {hasOnboarded && (
              <>
                <div className="mb-2 flex items-center justify-start">
                  <Text className="w-[50%] whitespace-nowrap pr-4">
                    Full Name
                  </Text>
                  <Text className="grow text-[#60676a]">{account.name}</Text>
                </div>
                <div className="mb-2 flex items-center justify-start">
                  <Text className="w-[50%] whitespace-nowrap pr-4">
                    Address
                  </Text>
                  <Text className="grow text-[#60676a]">
                    {[
                      details.street_address?.join(", "),
                      details.unit,
                      details.city,
                      details.state,
                      details.postal_code,
                    ]
                      .filter(Boolean)
                      .join(", ")}
                  </Text>
                </div>
              </>
            )}
            <h2 className="my-5 font-bold text-xl">Update Details</h2>
            <div>
              <Field label="Email">
                <Controller
                  name="email"
                  control={control}
                  rules={{ required: true }}
                  render={({ field }) => (
                    <Input {...field} disabled={isLegalEntity} />
                  )}
                />
                {isLegalEntity && (
                  <Text className="mt-2 text-gray-600 text-sm">
                    Please contact support@alpaca.markets to change the email
                    address for business accounts.
                  </Text>
                )}
              </Field>
            </div>
            {hasOnboarded && (
              <div className="mt-4">
                <Field label="Phone Number">
                  <Controller
                    name="phone_number"
                    control={control}
                    rules={{ required: true }}
                    render={({ field: { onChange, value, ...more } }) => (
                      <PhoneInput
                        {...more}
                        inputClass="bg-white rounded-lg text-sm border border-gray-200 text-gray-900 placeholder-gray-500"
                        placeholder="+1 (123) 456-7890"
                        onChange={onChange}
                        value={value}
                        country={iso
                          .alpha3ToAlpha2(
                            // this is the only country we will have at first
                            details.country_of_tax_residence || "USA",
                          )
                          ?.toLowerCase()}
                      />
                    )}
                  />
                </Field>
              </div>
            )}
            <Button
              className="mt-8 mr-auto"
              disabled={!isDirty}
              type="submit"
              form="form--basic"
            >
              Save changes
            </Button>
          </div>
        </form>
        <Request />
      </div>
    </UpdateEmailContext.Provider>
  );
};
